import { adjustColor, contrastColor } from '../../utils/ids-color-utils/ids-color-utils';

/**
 * Handle Personalization colors (and personalization related things)
 */
class IdsPersonalization {
  /** Holds the personalization color */
  state = { color: '#fff', useColorProgression: false };

  /** Color name to hex value mapping */
  private colorMap: Record<string, string> = {
    alabaster: '',
    amber: '#F98300',
    amethyst: '#7928E1',
    azure: '#0072ED',
    default: '',
    emerald: '#2AC371',
    graphite: '#6C6C6C',
    ruby: '#DA1217',
    slate: '#6F6F76',
    turquoise: '#40BDBE',
    white: '',
    black: '#000000',
    blue: '#0072ED',
    green: '#2AC371',
    red: '#DA1217',
    neutral: '#6F6F76',
    teal: '#40BDBE',
    purple: '#7928E1',
  };

  /**
   * Set the primary color and update the UI
   * @param {string} value the primary color
   */
  set color(value: string) {
    this.state.color = value;
    if (value.toLowerCase() === '#fff' || value.toLowerCase() === '#ffffff') {
      this.resetToDefault();
      return;
    }

    if (this.useColorProgression) {
      this.#appendColorProgressionStyleSheet();
      return;
    }
    document.querySelectorAll('ids-header').forEach((header) => header.setAttribute('color', value));
    document.querySelectorAll('ids-tabs[color-variant="module"]').forEach((tabs) => tabs.setAttribute('color', value));
  }

  /**
   * Get the current primary color
   * @returns {string} The current primary color hex value
   */
  get color(): string {
    return this.state.color;
  }

  /**
   * Get all available color names
   * @returns {string[]} Array of available color names
   */
  get colorNames(): string[] {
    return Object.keys(this.colorMap);
  }

  /**
   * Map a legacy Soho color or newer color name to the corresponding hex value.
   * @param {string} name the primary color name
   */
  set colorName(name: string) {
    const lowerName = name.toLowerCase();
    if (!(lowerName in this.colorMap)) {
      throw new Error(`Unknown color name: ${name}`);
    }
    const colorValue = this.colorMap[lowerName];
    if (colorValue === '') {
      this.resetToDefault();
    } else {
      this.color = colorValue;
    }
  }

  /**
   * Enable old method of color progression
   * @param {boolean} value whether to use color progression
   */
  set useColorProgression(value: boolean) {
    this.state.useColorProgression = value;
  }

  /**
   * Determine if using the old method of color progression
   * @returns {boolean} true if using color progression
   */
  get useColorProgression(): boolean {
    return this.state.useColorProgression;
  }

  /**
   * Calculate color progression based on a single color
   * @param {string} color the primary color
   * @returns {Record<string, string>} Object containing color progression values
   */
  colorProgression(color: string) {
    return {
      primary: color,
      primary10: adjustColor(color, 0.1),
      primary20: adjustColor(color, 0.3),
      primary40: adjustColor(color, 0.45),
      primary30: adjustColor(color, 0.55),
      primary50: adjustColor(color, 0.70),
      primary60: color,
      primary70: adjustColor(color, -0.20),
      primary80: adjustColor(color, -0.30),
      primary90: adjustColor(color, -0.40),
      primary100: adjustColor(color, -0.55),
      contrast: contrastColor(color, '#f5f5f5', '#161618')
    };
  }

  /** Append the personalization stylesheet */
  #appendColorProgressionStyleSheet() {
    const colors = this.colorProgression(this.state.color);
    const themeStyles = `:root, :host {
      --ids-color-primary: ${colors.primary};
      --ids-color-primary-10: ${colors.primary10};
      --ids-color-primary-20: ${colors.primary20};
      --ids-color-primary-30: ${colors.primary30};
      --ids-color-primary-40: ${colors.primary40};
      --ids-color-primary-50: ${colors.primary50};
      --ids-color-primary-60: ${colors.primary};
      --ids-color-primary-70: ${colors.primary70};
      --ids-color-primary-80: ${colors.primary80};
      --ids-color-primary-90: ${colors.primary90};
      --ids-color-primary-100: ${colors.primary100};
      --ids-header-input-color-border-hover: var(--ids-color-transparent);
      --ids-header-color-background: var(--ids-color-primary);
      --ids-header-color-border-bottom: var(--ids-color-primary-80);
      --ids-header-color-text: ${colors.contrast};
      --ids-header-button-color-text-default: ${colors.contrast};
      --ids-header-button-color-background-hover: ${colors.contrast === '#ffffff' ? 'rgba(255 255 255 / 0.2)' : 'rgba(0 0 0 / 0.1)'};
      --ids-header-button-color-border-hover: var(--ids-color-transparent);
      --ids-header-button-color-text-hover: ${colors.contrast};
      --ids-header-button-color-text-disabled: ${adjustColor(colors.contrast, -0.20)};
      --ids-header-button-shadow-focus: 0px 0px 0px 1px var(--ids-color-primary), 0px 0px 0px 1px var(--ids-color-primary);
      --ids-header-button-color-border-focus: ${colors.contrast};
      --ids-header-button-color-background-pressed: ${colors.contrast === '#ffffff' ? 'rgba(255 255 255 / 0.2)' : 'rgba(0 0 0 / 0.1)'};
      --ids-header-button-color-border-pressed: var(--ids-color-transparent);
      --ids-header-button-color-text-pressed: ${colors.contrast};
      --ids-header-button-menu-color-background-active: ${colors.contrast === '#ffffff' ? 'rgba(255 255 255 / 0.2)' : 'rgba(0 0 0 / 0.1)'};
      --ids-header-button-menu-color-active: ${colors.contrast};
      --ids-button-primary-color-background-default: var(--ids-color-primary);
      --ids-button-primary-color-border-default: var(--ids-color-primary);
      --ids-button-primary-color-background-hover: var(--ids-color-primary-80);
      --ids-button-primary-color-border-hover: var(--ids-color-primary-80);
      --ids-button-primary-color-background-disabled: var(--ids-color-primary);
      --ids-button-primary-color-border-disabled: var(--ids-color-primary);
      --ids-button-primary-color-border-focus: var(--ids-color-primary);
      --ids-button-primary-shadow-focus: 0 0 0 2px var(--ids-color-background-default), 0 0 0 3px var(--ids-color-primary-80);
      --ids-button-secondary-color-border-default: var(--ids-color-primary);
      --ids-button-secondary-color-text-default: var(--ids-color-primary);
      --ids-button-secondary-color-border-disabled: var(--ids-color-primary);
      --ids-button-secondary-color-text-disabled: var(--ids-color-primary);
      --ids-button-secondary-color-background-hover: var(--ids-color-primary-10);
      --ids-button-secondary-color-border-hover: var(--ids-color-primary-70);
      --ids-button-secondary-color-text-hover: var(--ids-color-primary-70);
      --ids-button-secondary-color-background-pressed: var(--ids-color-primary-10);
      --ids-button-secondary-color-border-pressed: var(--ids-color-primary-70);
      --ids-button-secondary-color-text-pressed: var(--ids-color-primary-70);
      --ids-button-secondary-shadow-focus: 0px 0px 0px 1px var(--ids-color-primary-80), 0px 0px 0px 1px var(--ids-color-primary-80);
      --ids-button-secondary-color-border-focus: var(--ids-color-primary-80);
      --ids-button-tertiary-shadow-focus: 0px 0px 0px 1px var(--ids-color-primary-80), 0px 0px 0px 1px var(--ids-color-primary-80);
      --ids-button-tertiary-color-border-focus: var(--ids-color-primary-80);
      --ids-checkbox-color-background-selected: var(--ids-color-primary);
      --ids-checkbox-color-border-selected: var(--ids-color-primary);
      --ids-checkbox-color-border-focus: var(--ids-color-primary);
      --ids-checkbox-shadow-focus: 0 0 0 2px var(--ids-color-background-default), 0 0 0 3px var(--ids-color-primary-80);
      --ids-input-color-border-focus: var(--ids-color-primary);
      --ids-shadow-focus: 0px 0px 6px 0px ${adjustColor(colors.primary, 0.4)};
      --ids-hyperlink-color-text-default: var(--ids-color-primary);
      --ids-hyperlink-color-text-hover: var(--ids-color-primary-80);
      --ids-progress-bar-fill-color-background: var(--ids-color-primary);
      --ids-tab-color-text-selected: var(--ids-color-primary);
      --ids-tab-color-text-selected: var(--ids-color-primary);
    }`;

    const doc = (document.head as any);
    const styleElem = document.querySelector('#ids-personalization');
    const style = styleElem || document.createElement('style');
    style.textContent = themeStyles;
    style.id = 'ids-personalization';
    style.setAttribute('nonce', (document as any).nonce);
    if (!styleElem) doc.appendChild(style);
  }

  /** Remove the personalization stylesheet and set to default color */
  resetToDefault() {
    if (this.useColorProgression) {
      const styleElem = document.querySelector('#ids-personalization');
      if (styleElem) styleElem.remove();
      return;
    }

    document.querySelectorAll('ids-header').forEach((header) => header.removeAttribute('color'));
    document.querySelectorAll('ids-tabs[color-variant="module"]').forEach((tabs) => tabs.removeAttribute('color'));
  }
}

export default IdsPersonalization;
